import { useEffect } from "react";
import { safeJsonLd } from "@/lib/safeJsonLd";

/**
 * SEO component using React 19's native head metadata hoisting.
 * No external dependency on react-helmet-async (which is broken with React 19).
 * Any <title>, <meta>, <link>, or <script type="application/ld+json"> rendered
 * inside a component is automatically moved into the <head> by React.
 */

const SITE_NAME = "Agriturismo Sadira";
const DEFAULT_TITLE =
  "Agriturismo Sadira | Bergamo | Camere, Ristorante, Fattoria Didattica ed Esperienze";
const DEFAULT_DESCRIPTION =
  "Agriturismo Sadira a Calusco d'Adda (Bergamo): camere accoglienti, ristorante con prodotti a Km Zero, fattoria didattica per bambini, esperienze e laboratori. Prenota online!";
const DEFAULT_IMAGE = "/favicon.png";
const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Via alle Cascine 525",
  addressLocality: "Calusco d'Adda",
  addressRegion: "BG",
  postalCode: "24033",
  addressCountry: "IT",
};

const ITALIAN_STOPWORDS = new Set([
  "di", "a", "da", "in", "con", "su", "per", "tra", "fra", "il", "lo", "la",
  "i", "gli", "le", "un", "uno", "una", "e", "ed", "o", "ma", "che", "non",
  "se", "ai", "al", "alla", "alle", "ad", "agli", "del", "della", "dei", "delle",
  "degli", "nel", "nella", "nei", "nelle", "negli", "sul", "sulla", "sui", "sulle",
  "sugli", "ci", "vi", "si", "ne", "ho", "hai", "ha", "abbiamo", "avete",
  "hanno", "sono", "sei", "siamo", "siete", "essere", "stato", "stata",
  "questo", "quella", "questa", "quello", "the", "and", "for", "with", "you",
  "your", "from", "this", "that",
]);

const extractKeywords = (texts, max = 12) => {
  const counts = new Map();
  texts
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/<[^>]*>/g, " ")
    .replace(/[^a-zà-ù\s]/gi, " ")
    .split(/\s+/)
    .forEach((w) => {
      if (!w || w.length < 4) return;
      if (ITALIAN_STOPWORDS.has(w)) return;
      counts.set(w, (counts.get(w) || 0) + 1);
    });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([w]) => w);
};

const stripHtml = (s) =>
  String(s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const truncate = (s, n) => {
  const t = stripHtml(s);
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
};

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  event = null,
  restaurant = null,
  breadcrumbs = null,
  noindex = false,
}) => {
  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : "https://agriturismosadira.it";

  // React 19 hoists <meta> tags but does NOT deduplicate by name/property.
  // Static tags declared in public/index.html would win. This effect strips them
  // so per-page dynamic tags become the source of truth.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const selectors = [
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[name="title"]',
      'meta[name="twitter:card"]',
      'meta[name="twitter:url"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
      'meta[name="twitter:image"]',
      'meta[property="og:type"]',
      'meta[property="og:url"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:image"]',
      'meta[property="og:site_name"]',
      'meta[property="og:locale"]',
      'link[rel="canonical"]',
    ];
    selectors.forEach((sel) => {
      const nodes = document.head.querySelectorAll(sel);
      // Keep only the LAST one (React 19 appended dynamic tags last)
      for (let i = 0; i < nodes.length - 1; i += 1) {
        nodes[i].parentNode?.removeChild(nodes[i]);
      }
    });
  });

  let computedTitle = title;
  let computedDescription = description;
  let computedKeywords = keywords;
  let computedImage = image;
  let computedType = type;

  if (event) {
    computedTitle = computedTitle || `${event.title} | Esperienze a Bergamo`;
    const baseDesc = event.short_description || event.description || "";
    computedDescription = computedDescription || truncate(baseDesc, 160);
    computedImage = computedImage || event.image_url;
    computedType = "event";
    if (!computedKeywords) {
      const auto = extractKeywords(
        [event.title, event.category, event.location, baseDesc, "esperienze bergamo agriturismo"],
        15
      );
      computedKeywords = auto.join(", ");
    }
  }

  const fullTitle = computedTitle ? `${computedTitle} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullDescription = truncate(computedDescription || DEFAULT_DESCRIPTION, 300);
  const fullImage = computedImage || DEFAULT_IMAGE;
  const fullUrl = url
    ? url.startsWith("http") ? url : `${baseUrl}${url}`
    : (typeof window !== "undefined" ? window.location.href : baseUrl);

  const eventSchema = event
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.title,
        description: stripHtml(event.description || event.short_description || ""),
        startDate: event.start_date || event.fixed_date || event.date,
        endDate: event.end_date || event.fixed_date || event.date,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: event.is_online
          ? "https://schema.org/OnlineEventAttendanceMode"
          : "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: event.location_name || SITE_NAME,
          address: ADDRESS,
          geo: { "@type": "GeoCoordinates", latitude: 45.6857, longitude: 9.4671 },
        },
        image: fullImage,
        offers: event.price !== undefined && event.price !== null
          ? {
              "@type": "Offer",
              price: String(event.price),
              priceCurrency: "EUR",
              availability: "https://schema.org/InStock",
              url: fullUrl,
              validFrom: event.created_at || new Date().toISOString(),
            }
          : undefined,
        organizer: { "@type": "Organization", name: SITE_NAME, url: baseUrl },
        performer: { "@type": "Organization", name: SITE_NAME },
      }
    : null;

  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url?.startsWith("http") ? b.url : `${baseUrl}${b.url || ""}`,
        })),
      }
    : null;

  // Restaurant / LocalBusiness schema (for pages like "Ristorante Agricolo")
  const restaurantSchema = restaurant
    ? (() => {
        const it2en = {
          "lunedi": "Mo", "lunedì": "Mo", "monday": "Mo",
          "martedi": "Tu", "martedì": "Tu", "tuesday": "Tu",
          "mercoledi": "We", "mercoledì": "We", "wednesday": "We",
          "giovedi": "Th", "giovedì": "Th", "thursday": "Th",
          "venerdi": "Fr", "venerdì": "Fr", "friday": "Fr",
          "sabato": "Sa", "saturday": "Sa",
          "domenica": "Su", "sunday": "Su",
        };
        const parseMealTimes = (mealText) => {
          const t = (mealText || "").toLowerCase();
          const opens = [];
          const closes = [];
          if (/pranzo|lunch/.test(t)) { opens.push("12:00"); closes.push("14:30"); }
          if (/cena|dinner|serata/.test(t)) { opens.push("19:00"); closes.push("22:30"); }
          if (opens.length === 0) return null;
          return { opens, closes };
        };
        const openingHours = (restaurant.opening_days || [])
          .flatMap((d) => {
            const dayKey = String(d.day_it || d.day_en || "").trim().toLowerCase().split(/\s+/)[0];
            const code = it2en[dayKey];
            if (!code) return [];
            const tt = parseMealTimes(d.meal_it || d.meal_en);
            if (!tt) return [`${code} 12:00-22:30`];
            return tt.opens.map((o, i) => `${code} ${o}-${tt.closes[i]}`);
          });

        return {
          "@context": "https://schema.org",
          "@type": "Restaurant",
          name: restaurant.name || `${SITE_NAME} — Ristorante Agricolo`,
          description: stripHtml(restaurant.description || fullDescription),
          image: restaurant.image || fullImage,
          url: fullUrl,
          telephone: restaurant.telephone || undefined,
          servesCuisine: restaurant.servesCuisine || ["Cucina Bergamasca", "Italian", "Farm-to-table"],
          priceRange: restaurant.priceRange || "€€",
          address: ADDRESS,
          geo: { "@type": "GeoCoordinates", latitude: 45.6857, longitude: 9.4671 },
          openingHours: openingHours.length > 0 ? openingHours : undefined,
          hasMenu: restaurant.menu_url || undefined,
          acceptsReservations: true,
          keywords: computedKeywords,
        };
      })()
    : null;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={fullDescription} />
      {computedKeywords && <meta name="keywords" content={computedKeywords} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}
      <link rel="canonical" href={fullUrl} />

      <link rel="alternate" hrefLang="it" href={fullUrl} />
      <link
        rel="alternate"
        hrefLang="en"
        href={`${fullUrl}${fullUrl.includes("?") ? "&" : "?"}lang=en`}
      />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      <meta property="og:type" content={computedType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="it_IT" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />

      {eventSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeJsonLd(eventSchema) }}
        />
      )}
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeJsonLd(breadcrumbSchema) }}
        />
      )}
      {restaurantSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeJsonLd(restaurantSchema) }}
        />
      )}
    </>
  );
};

export default SEO;
